/**
 * FHIR R5 Medication Service - Frontend API Client
 * Complete API integration for FHIR-compliant medication management
 */

import type {
  FHIRApiResponse,
  FHIRBundle,
  FHIRMedicationStatement,
  FHIRMedicationStats,
  MedicationCreateRequest,
  MedicationDisplay,
  MedicationQueryParams,
  MedicationUpdateRequest,
} from "@/types/fhir";
import { apiHelpers } from "../api";

// Note: apiHelpers already has baseURL '/api', so we don't include it here
const FHIR_BASE_URL = "/fhir/medications";

const buildBundleFromRows = (rows: any[] = []): FHIRBundle => ({
  resourceType: "Bundle",
  type: "collection",
  entry: rows.map((row) => {
    const rawData = row?.data ?? row;
    const medicationStatement = rawData?.medicationStatement ?? rawData;
    const medication = rawData?.medication;

    return {
      resource: {
        ...medicationStatement,
        id: medicationStatement?.id || String(row?.id || ""),
        resourceType: "MedicationStatement",
        medication,
      } as unknown as FHIRMedicationStatement,
    };
  }),
});

const extractRowsResponse = async (): Promise<any[]> => {
  const response = await apiHelpers.get<{
    success: boolean;
    data?: { medications?: any[] };
  }>(FHIR_BASE_URL);
  return response.data?.data?.medications || [];
};

/**
 * API Response Types
 */
interface FHIRBundleResponse {
  success: boolean;
  message: string;
  data: {
    resourceCount: number;
    bundle: FHIRBundle;
  };
}

interface FHIRStatsResponse {
  success: boolean;
  data: FHIRMedicationStats;
}

interface FHIRTerminologiesResponse {
  success: boolean;
  data: {
    categories: { code: string; display: string; system: string }[];
    routes: { code: string; display: string; system: string }[];
    frequencies: { code: string; display: string; system: string }[];
  };
}

/**
 * FHIR Medication Service
 * All API calls to FHIR R5 medication endpoints
 */
export const fhirMedicationService = {
  /**
   * Get all medications for current user
   * @param params - Query parameters (status, category, date range, pagination)
   * @returns FHIR Bundle with MedicationStatement resources
   */
  getAllMedications: async (
    params?: MedicationQueryParams,
  ): Promise<FHIRBundleResponse> => {
    let rows = await extractRowsResponse();

    if (params?.status?.length) {
      const allowed = new Set(params.status);
      rows = rows.filter((row) => {
        const status =
          row?.data?.medicationStatement?.status || row?.data?.status;
        return allowed.has(status);
      });
    }

    if (params?.search) {
      const needle = params.search.toLowerCase();
      rows = rows.filter((row) => {
        const data = row?.data ?? {};
        const statement = data?.medicationStatement ?? data;
        const medication = data?.medication ?? {};
        const name =
          statement?.medicationCodeableConcept?.text ||
          medication?.tradeName ||
          "";
        return String(name).toLowerCase().includes(needle);
      });
    }

    const bundle = buildBundleFromRows(rows);
    return {
      success: true,
      message: "OK",
      data: {
        resourceCount: rows.length,
        bundle,
      },
    };
  },

  /**
   * Get active medications only
   * @returns FHIR Bundle with active MedicationStatement resources
   */
  getActiveMedications: async (): Promise<FHIRBundleResponse> => {
    const rows = await extractRowsResponse();
    const filtered = rows.filter((row) => {
      const status =
        row?.data?.medicationStatement?.status || row?.data?.status;
      return status === "active" || status === "recorded";
    });

    return {
      success: true,
      message: "OK",
      data: {
        resourceCount: filtered.length,
        bundle: buildBundleFromRows(filtered),
      },
    };
  },

  /**
   * Get medications by category
   * @param category - SNOMED CT category code (e.g., 'analgesic', 'antibiotic')
   * @returns FHIR Bundle with MedicationStatement resources
   */
  getMedicationsByCategory: async (
    category: string,
  ): Promise<FHIRBundleResponse> => {
    const rows = await extractRowsResponse();
    const filtered = rows.filter((row) => {
      const coding =
        row?.data?.medicationStatement?.medicationCodeableConcept?.coding || [];
      return coding.some(
        (c: any) => c?.code === category || c?.display === category,
      );
    });

    return {
      success: true,
      message: "OK",
      data: {
        resourceCount: filtered.length,
        bundle: buildBundleFromRows(filtered),
      },
    };
  },

  /**
   * Get medications expiring soon (within 30 days)
   * @returns FHIR Bundle with MedicationStatement resources
   */
  getExpiringMedications: async (): Promise<FHIRBundleResponse> => {
    const rows = await extractRowsResponse();
    const now = new Date();
    const threshold = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    const filtered = rows.filter((row) => {
      const end =
        row?.data?.medicationStatement?.effectivePeriod?.end ||
        row?.data?.medicationStatement?.medicationPeriod?.end;
      if (!end) return false;
      const endDate = new Date(end);
      return endDate >= now && endDate <= threshold;
    });

    return {
      success: true,
      message: "OK",
      data: {
        resourceCount: filtered.length,
        bundle: buildBundleFromRows(filtered),
      },
    };
  },

  /**
   * Get single medication by ID
   * @param id - FHIR MedicationStatement ID
   * @returns Single FHIR MedicationStatement resource
   */
  getMedicationById: async (
    id: string,
  ): Promise<FHIRApiResponse<FHIRMedicationStatement>> => {
    const numericId = Number(id);

    if (!Number.isNaN(numericId)) {
      const response = await apiHelpers.get<{
        success: boolean;
        data?: { resource?: any };
      }>(`/fhir/resources/${numericId}`);
      const row = response.data?.data?.resource;
      if (row) {
        const rawData = row?.data ?? row;
        const statement = rawData?.medicationStatement ?? rawData;
        const medication = rawData?.medication;
        return {
          success: true,
          data: {
            ...statement,
            id: statement?.id || String(row?.id || id),
            resourceType: "MedicationStatement",
            medication,
          } as unknown as FHIRMedicationStatement,
        } as FHIRApiResponse<FHIRMedicationStatement>;
      }
    }

    const rows = await extractRowsResponse();
    const found = rows.find((row) => {
      const statementId =
        row?.data?.medicationStatement?.id ||
        row?.data?.id ||
        String(row?.id || "");
      return String(statementId) === String(id);
    });

    if (!found) {
      throw new Error("Medication not found");
    }

    const rawData = found?.data ?? found;
    const statement = rawData?.medicationStatement ?? rawData;
    const medication = rawData?.medication;
    return {
      success: true,
      data: {
        ...statement,
        id: statement?.id || String(found?.id || id),
        resourceType: "MedicationStatement",
        medication,
      } as unknown as FHIRMedicationStatement,
    } as FHIRApiResponse<FHIRMedicationStatement>;
  },

  /**
   * Create new medication
   * @param medicationData - Medication creation data
   * @returns Created FHIR MedicationStatement resource
   */
  createMedication: async (
    medicationData: MedicationCreateRequest,
  ): Promise<FHIRApiResponse<FHIRMedicationStatement>> => {
    // Transform simplified request to FHIR R5 MedicationStatement structure
    const fhirPayload = {
      medicationStatement: {
        medicationCodeableConcept: {
          text: medicationData.name,
          coding: medicationData.category
            ? [
                {
                  system: "http://snomed.info/sct",
                  code: medicationData.category,
                  display: medicationData.category,
                },
              ]
            : undefined,
        },
        status: "active",
        effectivePeriod: {
          start: medicationData.startDate,
          end: medicationData.endDate,
        },
        dosage: medicationData.dosageText
          ? [
              {
                text: medicationData.dosageText,
                route: medicationData.routeCode
                  ? {
                      coding: [
                        {
                          system: "http://snomed.info/sct",
                          code: medicationData.routeCode,
                        },
                      ],
                    }
                  : undefined,
                timing: medicationData.frequencyCode
                  ? {
                      code: {
                        coding: [
                          {
                            system:
                              "http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation",
                            code: medicationData.frequencyCode,
                          },
                        ],
                      },
                    }
                  : undefined,
              },
            ]
          : [],
        note: medicationData.instructions
          ? [
              {
                text: medicationData.instructions,
              },
            ]
          : undefined,
      },
    };

    const response = await apiHelpers.post<{
      success: boolean;
      data?: { medication?: any };
    }>(FHIR_BASE_URL, fhirPayload);
    const row = response.data?.data?.medication;
    const rawData = row?.data ?? row;
    const statement = rawData?.medicationStatement ?? rawData;
    const medication = rawData?.medication;
    return {
      success: true,
      data: {
        ...statement,
        id: statement?.id || String(row?.id || ""),
        resourceType: "MedicationStatement",
        medication,
      } as unknown as FHIRMedicationStatement,
    } as FHIRApiResponse<FHIRMedicationStatement>;
  },

  /**
   * Update existing medication
   * @param id - FHIR MedicationStatement ID
   * @param updateData - Medication update data
   * @returns Updated FHIR MedicationStatement resource
   */
  updateMedication: async (
    id: string,
    updateData: MedicationUpdateRequest,
  ): Promise<FHIRApiResponse<FHIRMedicationStatement>> => {
    const response = await apiHelpers.put<{
      success: boolean;
      data?: { resource?: any };
    }>(`/fhir/resources/${id}`, { data: updateData });
    const row = response.data?.data?.resource;
    const rawData = row?.data ?? row;
    const statement = rawData?.medicationStatement ?? rawData;
    const medication = rawData?.medication;
    return {
      success: true,
      data: {
        ...statement,
        id: statement?.id || String(row?.id || id),
        resourceType: "MedicationStatement",
        medication,
      } as unknown as FHIRMedicationStatement,
    } as FHIRApiResponse<FHIRMedicationStatement>;
  },

  /**
   * Delete medication
   * @param id - FHIR MedicationStatement ID
   * @returns Deletion confirmation
   */
  deleteMedication: async (id: string): Promise<FHIRApiResponse<null>> => {
    await apiHelpers.delete(`/fhir/resources/${id}`);
    return {
      success: true,
      data: null,
    } as FHIRApiResponse<null>;
  },

  /**
   * Get medication statistics
   * @returns Aggregated medication statistics
   */
  getStatistics: async (): Promise<FHIRStatsResponse> => {
    const rows = await extractRowsResponse();
    const total = rows.length;
    const active = rows.filter((row) => {
      const status =
        row?.data?.medicationStatement?.status || row?.data?.status;
      return status === "active" || status === "recorded";
    }).length;

    return {
      success: true,
      data: {
        total,
        active,
        inactive: Math.max(total - active, 0),
        expiringSoon: 0,
        byCategory: {},
      } as unknown as FHIRMedicationStats,
    };
  },

  /**
   * Get FHIR terminologies (categories, routes, frequencies)
   * @returns Available FHIR code systems for medication management
   */
  getTerminologies: async (): Promise<FHIRTerminologiesResponse> => {
    return {
      success: true,
      data: {
        categories: [],
        routes: [],
        frequencies: [],
      },
    };
  },

  /**
   * Convert legacy medication to FHIR format
   * @param legacyId - Legacy medication ID from old system
   * @returns Converted FHIR MedicationStatement resource
   */
  convertLegacyMedication: async (
    _legacyId: string,
  ): Promise<FHIRApiResponse<FHIRMedicationStatement>> => {
    throw new Error(
      "Legacy medication conversion endpoint is not available in this backend configuration",
    );
  },
};

/**
 * HealthPass stores extra medication metadata alongside the FHIR statement -
 * the stored resource is `{ medicationStatement, medication }`, where the
 * second object is this sidecar, not the FHIR `medication` element. See
 * getMedicationPayload in backend/src/routes/fhir-fastify.ts.
 */
interface MedicationSidecar {
  tradeName?: string;
  activeIngredient?: string;
  manufacturerName?: string;
  internationalCode?: { system?: string; code?: string; display?: string };
  images?: { packageImage?: string; medicationImage?: string };
}

/** A stored statement may also carry a structured dosage HealthPass writes. */
type StoredMedicationStatement = FHIRMedicationStatement & {
  dosageDetail?: { value?: number | string; unit?: string };
};

/**
 * The sidecar and the FHIR `medication` element share a property name. The
 * FHIR one is a CodeableConcept and therefore has `coding`; the sidecar never
 * does.
 */
function medicationSidecarOf(
  statement: StoredMedicationStatement,
): MedicationSidecar | undefined {
  const value = statement.medication;
  if (!value || typeof value !== "object" || "coding" in value) {
    return undefined;
  }
  return value as MedicationSidecar;
}

/**
 * Helper Functions for UI Display
 */
export const medicationDisplayHelpers = {
  /**
   * Convert FHIR MedicationStatement to UI-friendly display format
   */
  toDisplayFormat: (
    statement: StoredMedicationStatement,
  ): MedicationDisplay => {
    // FHIR R5: Try medicationCodeableConcept first (preferred), then fall back to medication
    const medicationCode =
      statement.medicationCodeableConcept ||
      ("medication" in statement &&
      statement.medication &&
      "coding" in statement.medication
        ? statement.medication
        : undefined);

    const name =
      medicationCode?.text ||
      medicationCode?.coding?.[0]?.display ||
      "Unknown Medication";

    // Check for dosageDetail first (structured data with value and unit)
    const dosageDetail = statement.dosageDetail;
    const dosageText = dosageDetail
      ? `${dosageDetail.value} ${dosageDetail.unit || ""}`
      : statement.dosage?.[0]?.text ||
        statement.dosage?.[0]?.patientInstruction ||
        undefined;

    const frequency = statement.dosage?.[0]?.timing?.code?.text || undefined;

    // FHIR R5: effectivePeriod is directly on the statement
    const effectivePeriod = statement.effectivePeriod;

    // Extract extended medication fields and image URLs
    const sidecar = medicationSidecarOf(statement);
    const tradeName = sidecar?.tradeName;
    const activeIngredient = sidecar?.activeIngredient;
    const manufacturerName = sidecar?.manufacturerName;
    // MedicationDisplay.internationalCode is the bare PZN/ATC code; the stored
    // sidecar keeps the full coding. Assigning the object here would have put
    // `[object Object]` in front of a user had this display data been rendered.
    const internationalCode = sidecar?.internationalCode?.code;
    const packageImageUrl = sidecar?.images?.packageImage;
    const medicationImageUrl = sidecar?.images?.medicationImage;

    return {
      id: statement.id || "",
      name,
      status: statement.status,
      category:
        statement.category?.text || statement.category?.coding?.[0]?.display,
      dosage: dosageText,
      frequency,
      startDate: effectivePeriod?.start,
      endDate: effectivePeriod?.end,
      instructions: statement.dosage?.[0]?.patientInstruction,
      prescriber: undefined,
      pharmacy: undefined,
      refillsRemaining: undefined,
      expirationDate: undefined,
      sideEffects: undefined,
      interactions: undefined,
      tradeName,
      activeIngredient,
      manufacturerName,
      internationalCode,
      packageImageUrl,
      medicationImageUrl,
      raw: statement,
    };
  },

  /**
   * Extract all medications from FHIR Bundle
   */
  extractMedicationsFromBundle: (
    bundle: FHIRBundle,
  ): FHIRMedicationStatement[] => {
    if (!bundle.entry || bundle.entry.length === 0) {
      return [];
    }

    return bundle.entry
      .map((entry) => entry.resource)
      .filter(
        (resource) => resource?.resourceType === "MedicationStatement",
      ) as FHIRMedicationStatement[];
  },

  /**
   * Convert FHIR Bundle to display format array
   */
  bundleToDisplayArray: (bundle: FHIRBundle): MedicationDisplay[] => {
    const medications =
      medicationDisplayHelpers.extractMedicationsFromBundle(bundle);
    return medications.map((med) =>
      medicationDisplayHelpers.toDisplayFormat(med),
    );
  },

  /**
   * Format medication status for display
   */
  formatStatus: (status: string): string => {
    const statusMap: Record<string, string> = {
      active: "Aktiv",
      completed: "Abgeschlossen",
      stopped: "Gestoppt",
      "on-hold": "Pausiert",
      "entered-in-error": "Fehlerhafte Eingabe",
      intended: "Geplant",
      "not-taken": "Nicht eingenommen",
      unknown: "Unbekannt",
    };
    return statusMap[status] || status;
  },

  /**
   * Get status badge color for UI
   */
  getStatusColor: (status: string): string => {
    const colorMap: Record<string, string> = {
      active: "green",
      completed: "blue",
      stopped: "red",
      "on-hold": "yellow",
      intended: "purple",
      "not-taken": "gray",
      "entered-in-error": "red",
      unknown: "gray",
    };
    return colorMap[status] || "gray";
  },

  /**
   * Check if medication is expiring soon (within 30 days)
   */
  isExpiringSoon: (medication: FHIRMedicationStatement): boolean => {
    const effectivePeriod =
      medication.effective && typeof medication.effective !== "string"
        ? medication.effective
        : undefined;

    if (!effectivePeriod?.end) return false;

    const endDate = new Date(effectivePeriod.end);
    const today = new Date();
    const daysUntilExpiry = Math.ceil(
      (endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  },

  /**
   * Check if medication is expired
   */
  isExpired: (medication: FHIRMedicationStatement): boolean => {
    const effectivePeriod =
      medication.effective && typeof medication.effective !== "string"
        ? medication.effective
        : undefined;

    if (!effectivePeriod?.end) return false;

    const endDate = new Date(effectivePeriod.end);
    const today = new Date();

    return endDate < today;
  },
};

export default fhirMedicationService;
